import { logger } from "../utils/logger";
/**
 * Fetch utility with configurable timeout and better error handling
 * Prevents AbortError messages and provides graceful degradation
 */

export interface FetchWithTimeoutOptions extends RequestInit {
  timeout?: number; // milliseconds, default 30000 (30 seconds)
  showTimeoutError?: boolean; // whether to throw timeout errors, default false
}

export class FetchTimeoutError extends Error {
  constructor(message: string, public url: string, public timeoutMs: number) {
    super(message);
    this.name = 'FetchTimeoutError';
  }
}

export class FetchNetworkError extends Error {
  constructor(message: string, public url: string, public originalError?: any) {
    super(message);
    this.name = 'FetchNetworkError';
  }
}

/**
 * Fetch with timeout and better error handling
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options with optional timeout
 * @returns Promise<Response>
 * @throws {FetchTimeoutError} if timeout is exceeded and showTimeoutError is true
 * @throws {FetchNetworkError} for other network errors
 */
export async function fetchWithTimeout(
  url: string,
  options: FetchWithTimeoutOptions = {}
): Promise<Response> {
  const {
    timeout = 30000, // 30 seconds default
    showTimeoutError = false,
    ...fetchOptions
  } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);

    // Check if it's an abort error (timeout)
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      const timeoutError = new FetchTimeoutError(
        `Request timed out after ${timeout}ms`,
        url,
        timeout
      );

      if (showTimeoutError) {
        throw timeoutError;
      } else {
        // Silent timeout - log but don't throw
        logger.warn(`⏱️ Request timeout (${timeout}ms):`, url);
        // Return a fake 504 response for graceful handling
        return new Response(
          JSON.stringify({
            error: 'Request timeout',
            message: `The server did not respond within ${timeout}ms`,
            code: 'TIMEOUT'
          }),
          {
            status: 504,
            statusText: 'Gateway Timeout',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }
    }

    // Other network errors
    logger.error('🔴 Network error:', error.message, 'URL:', url);
    throw new FetchNetworkError(
      error.message || 'Network request failed',
      url,
      error
    );
  }
}

/**
 * Fetch JSON with timeout and automatic error handling
 * Returns null on errors instead of throwing (graceful degradation)
 */
export async function fetchJSON<T = any>(
  url: string,
  options: FetchWithTimeoutOptions = {}
): Promise<T | null> {
  try {
    const response = await fetchWithTimeout(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      logger.warn(`⚠️ HTTP ${response.status}:`, url);
      return null;
    }

    return await response.json();
  } catch (error) {
    logger.error('❌ fetchJSON failed:', url, error);
    return null;
  }
}

/**
 * Checks if the backend server is available
 * @returns boolean - true if server responds, false otherwise
 */
export async function isBackendAvailable(baseUrl: string): Promise<boolean> {
  try {
    const response = await fetchWithTimeout(`${baseUrl}/health`, {
      timeout: 5000,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || publicAnonKey}`,
      },
    });

    return response.ok;
  } catch {
    return false;
  }
}
